/**
 * The key a performance is in, for a file that does not say.
 *
 * Spelling needs a key: with none, every black key is written as a sharp, and a piece in E
 * flat comes out in D sharp. A file played in from a keyboard states none, so one is read
 * from how long each pitch class sounds, against the Krumhansl–Kessler key profiles -- the
 * standard reading, and a wrong answer from it is a neighbouring key, which spells almost
 * every note the same way.
 */

import type { TimedNote } from "./onsets";

const MAJOR = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const MINOR = [6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

/** The key signature of a major key on pitch class `tonic`, flats preferred past six. */
function fifthsOf(tonic: number): number {
  const sharps = (tonic * 7) % 12;
  return sharps > 6 ? sharps - 12 : sharps === 6 ? -6 : sharps;
}

function correlation(first: readonly number[], second: readonly number[]): number {
  const mean = (values: readonly number[]): number =>
    values.reduce((a, b) => a + b, 0) / values.length;
  const a = mean(first);
  const b = mean(second);
  let top = 0;
  let left = 0;
  let right = 0;
  first.forEach((value, index) => {
    const other = second[index] ?? 0;
    top += (value - a) * (other - b);
    left += (value - a) ** 2;
    right += (other - b) ** 2;
  });
  return left > 0 && right > 0 ? top / Math.sqrt(left * right) : 0;
}

/** The key the notes fit best, or `null` when nothing pitched was played. */
export function keyOf(notes: readonly TimedNote[]): { fifths: number; minor: boolean } | null {
  const classes = new Array<number>(12).fill(0);
  for (const note of notes) {
    if (!note.drum)
      classes[note.key % 12] = (classes[note.key % 12] ?? 0) + (note.end - note.start);
  }
  if (classes.every((amount) => amount === 0)) return null;
  let best = { score: -Infinity, fifths: 0, minor: false };
  for (let tonic = 0; tonic < 12; tonic += 1) {
    const turned = [...classes.slice(tonic), ...classes.slice(0, tonic)];
    const major = correlation(turned, MAJOR);
    const minor = correlation(turned, MINOR);
    if (major > best.score) best = { score: major, fifths: fifthsOf(tonic), minor: false };
    if (minor > best.score)
      best = { score: minor, fifths: fifthsOf((tonic + 3) % 12), minor: true };
  }
  return { fifths: best.fifths, minor: best.minor };
}
